/**
 * Which workspace subtree one `wayai pull` / `wayai push` invocation targets —
 * and the refusal when a single invocation names more than one.
 *
 * Three subtrees route here: `hubs/`, `bases/` and `communities/`. A community is
 * only ever named EXPLICITLY (gh #5003, Routing and scope): `communities/<x>`,
 * `--community <id|folder>`, or the community folder the current directory is
 * in. No repo default and no sole-candidate rule selects one, and a bare folder
 * name never resolves to one — so a bare `wayai pull`/`push` behaves exactly as it
 * did before in a repo that also holds community folders.
 *
 * `pull` and `push` are the one pair the Data merge neither deleted nor
 * namespaced: one verb each, routing by subtree. This module owns that
 * decision and nothing else. It resolves a SUBTREE (plus an explicitly-named
 * target when the invocation carried one) and hands off; picking WHICH hub or
 * WHICH base inside the chosen subtree stays with each side's own resolver, so
 * ambiguity within one subtree keeps that side's existing "say which one"
 * message rather than becoming this error.
 *
 * The decision is a pure function of the arguments and the workspace on disk, so
 * the routing matrix and the refusals are testable without a network or a login
 * — which is what makes the contract's "before any network call and before any
 * file write" placement checkable rather than asserted.
 */

import * as path from 'node:path';
import { SUBTREES, WAYAI_LAYOUT, resolveLayout, isDirectory, type ResolvedLayout, type Subtree } from './layout.js';
import { sanitizeTerminalText } from './terminal-output.js';
import { isDryRunToken, unreadDryRunRefusal } from './utils.js';
import { WORKSPACE_MANIFEST_LABEL, loadWorkspaceManifest } from './workspace-manifest.js';
import { filterPreviewHubs, scanNewHubs, scanWorkspaceHubs } from './workspace.js';
import { filterEditableBases, isUnder, listBaseFolders } from './base-workspace.js';

export type { Subtree };

export interface RepoDefaults {
  /** Folder name or hub id a bare `pull`/`push` targets in the `hubs/` subtree. */
  default_hub?: string;
  /** Folder name or base id a bare `pull`/`push` targets in the `bases/` subtree. */
  default_base?: string;
}

/**
 * Rule 4's repo defaults, from the workspace manifest `wayai-ws/wayai.yaml`
 * (`workspace-manifest.ts`).
 *
 * **The soft half of that file's posture split.** Absent, unreadable or
 * malformed all read as "no defaults declared" — a broken manifest must not make
 * `pull` fail before the subtree it would have selected can say anything useful.
 * The org binding sharing the file is read with the opposite posture
 * (`repo-config.ts`), and the two must not leak into each other: an
 * organization this reader cannot resolve is none of its business, and a
 * `default_base` it drops is none of the org reader's.
 *
 * A declared value must be a plain NAME, never a path. This file is committed,
 * so its contents are repo-authored input under the same threat model as the
 * workspace files: a `default_base: ../../../../tmp/x` would otherwise send a
 * bare `wayai pull` to write and prune outside the repo on a teammate's or a CI
 * machine. A value carrying a separator is dropped here rather than refused, so
 * one bad key cannot make every invocation in the repo fail before it can say
 * anything; the selector it would have supplied is simply absent, and the rules
 * below carry on.
 */
export function readRepoDefaults(gitRoot: string): RepoDefaults {
  const load = loadWorkspaceManifest(gitRoot);
  if (load.kind !== 'ok') return {};
  const raw = load.doc;
  const str = (v: unknown) => {
    if (typeof v !== 'string' || !v.trim()) return undefined;
    const value = v.trim();
    if (value.includes('/') || value.includes(path.sep) || value === '.' || value === '..') {
      console.warn(
        `Warning: ignoring ${JSON.stringify(value)} in ${WORKSPACE_MANIFEST_LABEL} — a default names a hub or base, not a path.`,
      );
      return undefined;
    }
    return value;
  };
  return { default_hub: str(raw.default_hub), default_base: str(raw.default_base) };
}

/**
 * The flags whose NEXT argument is a value rather than another token. Listed
 * explicitly (not inferred from a leading `-`) so the positional scan below
 * cannot mistake a flag's value for the invocation's target — a bare word taken
 * as the target would route the whole invocation by it.
 *
 * Covers every value-taking flag `pull`/`push` parse (`--hub`, `--base`,
 * `--community`, `--label`) plus `--org`, which the Data surface owns and a user
 * may reasonably try here. ADD A FLAG HERE in the same change that teaches
 * `pull`/`push` to take one.
 */
const VALUE_FLAGS = new Set(['--hub', '--base', '--community', '--label', '--org']);

/** Each subtree flag, with the target it names as `wayai --help` and its side's messages spell it. */
const SUBTREE_FLAGS = [
  { flag: '--hub', token: 'hubFlag', subtree: 'hubs', target: 'a hub', value: '<uuid|folder-name>' },
  { flag: '--base', token: 'baseFlag', subtree: 'bases', target: 'a base', value: '<id|folder-name>' },
  { flag: '--community', token: 'communityFlag', subtree: 'communities', target: 'a community', value: '<folder|community_id>' },
] as const;

/**
 * The flags only the communities side reads, per verb; any other target refuses them rather than
 * run without them. `--dry-run` is the bases side's too on push (a hub push refuses it in
 * `routeSubtree`), but no other side's on pull: a hub or base pull would write what the flag asked
 * it not to.
 */
const COMMUNITY_ONLY_FLAGS: Record<string, string[]> = { pull: ['--theirs', '--dry-run'], push: ['--theirs'] };

interface RoutingTokens {
  hubFlag?: string;
  baseFlag?: string;
  communityFlag?: string;
  /** The value `--org` was given, when one was. `routeSubtree` refuses `--org` however it was typed. */
  orgFlag?: string;
  positional?: string;
  /** A value flag typed as the last token, so no value followed it and none is recorded above. */
  trailingFlag?: string;
  /** The first subtree flag another value flag took as its value, so it was never read as a flag. */
  takenAsValue?: { flag: string; by: string };
}

/**
 * The routing-relevant tokens in an argv tail: the three subtree flags, `--org`,
 * and the first positional.
 *
 * BOTH spellings of a value flag are read — `--base crm` and `--base=crm`. They
 * are the same flag to every user, and the sibling `bases` tree is a commander
 * program, which accepts the `=` form, so the habit arrives here already formed.
 * Matching by exact token alone dropped the whole `--flag=value` at the `-`
 * skip: the `--org` refusal never fired, and an explicit `--base=crm` was
 * silently replaced by whatever rules 3–5 resolved to, including the other
 * subtree.
 */
export function parseRoutingTokens(args: string[]): RoutingTokens {
  const tokens: RoutingTokens = {};
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    // `--flag=value` is one token; split on the FIRST `=` so a value containing
    // one survives intact.
    const eq = arg.indexOf('=');
    const name = eq === -1 ? arg : arg.slice(0, eq);
    if (VALUE_FLAGS.has(name)) {
      const value = eq === -1 ? args[i + 1] : arg.slice(eq + 1);
      if (value !== undefined) {
        if (name === '--hub') tokens.hubFlag ??= value;
        if (name === '--base') tokens.baseFlag ??= value;
        if (name === '--community') tokens.communityFlag ??= value;
        if (name === '--org') tokens.orgFlag ??= value;
        // Only the space-separated form consumes the NEXT token.
        if (eq === -1) {
          const taken = value.split('=')[0];
          if (SUBTREE_FLAGS.some(({ flag }) => flag === taken)) tokens.takenAsValue ??= { flag: taken, by: name };
          i++;
        }
      } else {
        tokens.trailingFlag = name;
      }
      continue;
    }
    if (arg.startsWith('-')) continue;
    tokens.positional ??= arg;
  }
  return tokens;
}

/** Where a selector points. `both` and `unknown` are decided by rules further down. */
type SelectorSubtree =
  | { kind: Subtree; target: string }
  | { kind: 'both'; target: string }
  | { kind: 'unknown' };

/** First path segment of `p`, or `p` itself when it has none. */
function firstSegment(p: string): string {
  const normalized = p.split(path.sep).join('/');
  const [head] = normalized.split('/').filter(Boolean);
  return head ?? p;
}

/**
 * Resolve which subtree a selector names.
 *
 * Three forms, in order, because they answer for different repos:
 *
 *  1. A **subtree-qualified** selector (`bases/crm`, `wayai-ws/hubs/support`) —
 *     the form the refusal message itself hands the user. It names the subtree
 *     by the canonical subdir NAME, so it works before the folder exists (a
 *     first `pull` materializes it) and in a legacy repo whose hubs actually
 *     live in `workspace/`.
 *  2. A **real path** — resolved against cwd and tested for containment in the
 *     resolved dirs, so `../bases/crm` and an absolute path route too.
 *  3. A **bare name** — decided by which subtree already has a folder of that
 *     name. Present in both is an invocation naming targets in both subtrees:
 *     refusal (a), not a precedence. Only `hubs/` and `bases/` are asked: a
 *     community is named explicitly or not at all, so a bare name never reaches
 *     one (forms 1 and 2 do).
 *
 * A bare name matching neither is `unknown`: it does not decide the subtree, and
 * the rules below carry it to whichever subtree they select, where that side's
 * own resolver reports it missing in its own words.
 *
 * Exported for the worktree-scope commands (`wayai use` / `wayai unbind`), which
 * need this one decision — which subtree a selector names — and must NOT get the
 * rest of `routeSubtree`: rules 3–5 (cwd, repo defaults, sole candidate) exist so
 * a BARE `pull`/`push` finds a target, and a scope command that inherited them
 * would silently scope a worktree the user never named. The scope is a guard,
 * never a selector, so it takes the explicit forms only.
 */
export function resolveSelectorSubtree(
  layout: ResolvedLayout | null,
  selector: string,
  cwd: string,
): SelectorSubtree {
  const parts = selector.split(path.sep).join('/').split('/').filter(Boolean);
  const qualified = parts[0] === WAYAI_LAYOUT.wsDir ? parts.slice(1) : parts;
  if (qualified.length > 1) {
    if (qualified[0] === WAYAI_LAYOUT.hubsSubdir) return { kind: 'hubs', target: qualified[1] };
    if (qualified[0] === WAYAI_LAYOUT.basesSubdir) return { kind: 'bases', target: qualified[1] };
    if (qualified[0] === WAYAI_LAYOUT.communitiesSubdir) return { kind: 'communities', target: qualified[1] };
  }

  if (!layout) return { kind: 'unknown' };
  const { hubsDir, basesDir, communitiesDir } = layout;

  if (selector.includes('/') || selector.includes(path.sep)) {
    const abs = path.resolve(cwd, selector);
    if (isUnder(hubsDir, abs)) return { kind: 'hubs', target: firstSegment(path.relative(hubsDir, abs)) };
    if (isUnder(basesDir, abs)) return { kind: 'bases', target: firstSegment(path.relative(basesDir, abs)) };
    // The dir itself names no community, so it decides nothing.
    if (isUnder(communitiesDir, abs) && abs !== communitiesDir) {
      return { kind: 'communities', target: firstSegment(path.relative(communitiesDir, abs)) };
    }
    return { kind: 'unknown' };
  }

  const inHubs = isDirectory(path.join(hubsDir, selector));
  const inBases = isDirectory(path.join(basesDir, selector));
  if (inHubs && inBases) return { kind: 'both', target: selector };
  if (inHubs) return { kind: 'hubs', target: selector };
  if (inBases) return { kind: 'bases', target: selector };
  return { kind: 'unknown' };
}

/** A folder that could be a `pull`/`push` target: one either subtree would resolve to. */
interface Candidate {
  subtree: Subtree;
  name: string;
}

/**
 * Rule 5's candidate set: every folder either subtree could resolve to ON ITS
 * OWN.
 *
 * Delegated to each side's own scanner rather than re-derived here. A private
 * scan would have to reproduce what counts as a folder — the legacy `wayai.yaml`
 * identity fallback, the dot-prefix skip, the requirement that the identity file
 * actually parse and carry its id fields, the production-mirror drop — and any
 * divergence produces the worst possible failure for this module: a refusal
 * naming a target the subtree's own resolver does not see, whose two printed
 * recovery commands then both fail.
 *
 * Idless hub folders count too: `resolvePushTarget` treats created and
 * from-scratch folders as co-equal candidates, so a workspace holding one new
 * hub folder and one base folder genuinely spans both subtrees.
 */
function listCandidates(layout: ResolvedLayout): Candidate[] {
  const hubs = [
    ...filterPreviewHubs(scanWorkspaceHubs(layout.hubsDir)).map((h) => h.hubFolder),
    ...scanNewHubs(layout.hubsDir).map((h) => h.hubFolder),
  ];
  const bases = filterEditableBases(listBaseFolders(layout.basesDir)).map((b) => b.folder);
  return [
    ...hubs.map((folder) => ({ subtree: 'hubs' as const, name: path.basename(folder) })),
    ...bases.map((folder) => ({ subtree: 'bases' as const, name: path.basename(folder) })),
  ];
}

/** One subtree's target in a mixed invocation. */
export interface SubtreeTarget {
  subtree: Subtree;
  target: string;
}

const SUBDIR: Record<Subtree, string> = {
  hubs: WAYAI_LAYOUT.hubsSubdir,
  bases: WAYAI_LAYOUT.basesSubdir,
  communities: WAYAI_LAYOUT.communitiesSubdir,
};

/**
 * The refusal. One text for every case: each is an invocation that named a
 * target in more than one subtree, and what the user needs is the
 * single-subtree invocations that replace it — one per subtree named, listed in
 * subtree order however they were typed.
 */
export function mixedInvocationRefusal(verb: string, targets: SubtreeTarget[]): string {
  const named = [...targets]
    .sort((a, b) => SUBTREES.indexOf(a.subtree) - SUBTREES.indexOf(b.subtree))
    .map(({ subtree, target }) => ({ dir: `${SUBDIR[subtree]}/`, target: sanitizeTerminalText(target) }));
  const width = Math.max(...named.map(({ dir }) => dir.length));
  return [
    `Refusing a mixed ${verb}: this invocation spans ${named.length === 2 ? 'both' : named.length} subtrees.`,
    ...named.map(({ dir, target }) => `  ${dir.padEnd(width)} -> ${target}`),
    '',
    'Run one at a time:',
    ...named.map(({ dir, target }) => `  wayai ${verb} ${dir}${target}`),
  ].join('\n');
}

/** The refusal for a hub target beside a base target — the two subtrees rules 4 and 5 span. */
export function hubAndBaseRefusal(verb: string, hubTarget: string, baseTarget: string): string {
  return mixedInvocationRefusal(verb, [
    { subtree: 'hubs', target: hubTarget },
    { subtree: 'bases', target: baseTarget },
  ]);
}

/** The subtree flags an invocation carried. Shared with the scope selector. */
export function subtreeFlags(
  tokens: Pick<RoutingTokens, 'hubFlag' | 'baseFlag' | 'communityFlag'>,
): SubtreeTarget[] {
  return SUBTREE_FLAGS.flatMap(({ token, subtree }) => {
    const target = tokens[token];
    return target === undefined ? [] : [{ subtree, target }];
  });
}

export type RouteResult =
  | { ok: true; subtree: Subtree; selector?: string }
  | { ok: false; refusal: string };

export interface RouteOptions {
  /** `pull` or `push` — the verb the refusal text names. */
  verb: string;
  args: string[];
  gitRoot: string | null;
  cwd?: string;
}

/**
 * Route one invocation to exactly one subtree, or refuse it.
 *
 * The five rules, in order:
 *
 *  1. an explicit `--hub` / `--base` / `--community` flag — it names both the
 *     subtree and the target, so it wins over everything below;
 *  2. an explicit positional selector;
 *  3. the folder enclosing the current directory;
 *  4. the repo default from `wayai-ws/wayai.yaml`;
 *  5. the sole candidate folder, counted across `hubs/` and `bases/`.
 *
 * Rules 4 and 5 never select `communities/`: a community is named by rules 1–3
 * or not at all.
 *
 * and the three refusals — (a) targets named in more than one subtree, (b) rules
 * 1–5 leaving candidates that span `hubs/` and `bases/`, (c) more than one
 * subtree flag passed. A refusal is never a merge and never a silent pick. A subtree
 * flag or a positional with no target is refused, as is a flag only the communities side reads
 * (`COMMUNITY_ONLY_FLAGS`) with any other target, `--dry-run` with a hub push, and a valued
 * `--dry-run` with any target.
 *
 * When every rule is silent — nothing named, cwd outside the workspace, no
 * defaults, no candidate folders anywhere — this returns the `hubs` subtree.
 * Routing stops at rule 5 because that state names no target in either subtree, so
 * it is not a mixed invocation; routing to `hubs` keeps a bare `wayai pull` in
 * an empty repo answering with the hub side's existing "pass --hub <uuid> to
 * fetch a hub for the first time", which is the actionable message.
 */
export function routeSubtree(options: RouteOptions): RouteResult {
  const result = routeToSubtree(options);
  if (!result.ok || result.subtree === 'communities') return result;
  const communityOnly = (COMMUNITY_ONLY_FLAGS[options.verb] ?? []).find((flag) => options.args.includes(flag));
  if (communityOnly !== undefined) {
    return {
      ok: false,
      refusal: [
        // States only what holds for every flag and verb in the table: this target does not read the
        // flag, and a community's pull does (`--dry-run` is also read by some pushes, `--theirs` by none).
        `\`wayai ${options.verb}\` of ${SUBDIR[result.subtree]}/ does not read \`${communityOnly}\`, so it is refused rather than run without it. A community's pull takes it:`,
        `  wayai pull ${SUBDIR.communities}/<folder> ${communityOnly}`,
      ].join('\n'),
    };
  }
  // A hub push reads no `--dry-run` either, though a base push does, so the table passes it through.
  if (options.verb === 'push' && result.subtree === 'hubs' && options.args.includes('--dry-run')) {
    return {
      ok: false,
      refusal: [
        `\`wayai push\` of ${SUBDIR.hubs}/ does not read \`--dry-run\`, so it is refused rather than run without it. For a hub already created (a \`hub_id\` in its hub.yaml), \`wayai diff\` lists the changes without pushing them:`,
        '  wayai diff --hub <uuid|folder-name>',
      ].join('\n'),
    };
  }
  return result;
}

function routeToSubtree(options: RouteOptions): RouteResult {
  const { verb, args, gitRoot } = options;
  const cwd = options.cwd ?? process.cwd();
  const tokens = parseRoutingTokens(args);
  const { hubFlag, baseFlag, communityFlag, positional, orgFlag } = tokens;

  // `--org` belongs to the `wayai bases …` tree, which publishes it through the
  // commander program's `preAction` hook. These verbs never enter that program,
  // so an accepted `--org` would be silently discarded and the invocation would
  // run against the repo's OWN org — applying, and with `--prune` deleting,
  // against a same-named base in the wrong org while reporting success. Base ids
  // are org-scoped slugs, so a same-named base in both orgs is ordinary. Refused
  // rather than threaded: it is not a documented flag on either verb. Keyed on the
  // token, not on a recorded value: typed last, or taken as `--label`'s value, it
  // records none, and the invocation would run bare.
  if (orgFlag !== undefined || args.includes('--org')) {
    return {
      ok: false,
      refusal: [
        `\`wayai ${verb}\` does not take --org: it operates on the organization named by ${WORKSPACE_MANIFEST_LABEL}.`,
        '',
        'To act on another organization:',
        '  wayai bases <command> --org <uuid>   # the Data surface honors it',
        '  # or run from a checkout whose workspace manifest names that organization',
      ].join('\n'),
    };
  }
  // Every side reads `--dry-run` by its exact token, so a valued one (`--dry-run=true`) is read by none
  // and the invocation would apply what it asked only to list.
  const valuedDryRun = args.find((arg) => isDryRunToken(arg) && arg !== '--dry-run');
  if (valuedDryRun !== undefined) {
    return { ok: false, refusal: unreadDryRunRefusal(`wayai ${verb}`, valuedDryRun, true) };
  }
  // A subtree flag typed with no target — an unset variable in a script. Taken as another flag's
  // value (`--label $LABEL --hub $HUB`), it is never read as a flag, and what follows routes without
  // it; checked first, because the flag that took it is the one that kept no value. Typed last, it
  // records no value, leaving a bare invocation that rules 3–5 would send to whatever folder they
  // find, in any subtree; empty (`--hub=`, `--hub ""`), its side would read no selector and take the
  // folder the current directory is in, or the sole one. With `-y`, each would be written over unnamed.
  if (tokens.takenAsValue) {
    const { flag, by } = tokens.takenAsValue;
    return {
      ok: false,
      refusal: `\`wayai ${verb} ${by}\` took \`${flag}\` as its value, so \`${flag}\` is not read. Give ${by} a value, or leave it out.`,
    };
  }
  const empty = SUBTREE_FLAGS.find(({ flag, token }) => tokens[token] === '' || tokens.trailingFlag === flag);
  if (empty) {
    return {
      ok: false,
      refusal: `\`wayai ${verb} ${empty.flag}\` needs ${empty.target}, and none followed it.\n  wayai ${verb} ${empty.flag} ${empty.value}`,
    };
  }

  // Resolved ONCE: every rule below asks the same layout, and each resolve stats
  // the candidate dirs and lstat-walks the ones it uses.
  const layout = gitRoot ? resolveLayout(gitRoot) : null;

  // (c) — more than one subtree flag. Unambiguous from the arguments alone.
  const flagged = subtreeFlags({ hubFlag, baseFlag, communityFlag });
  if (flagged.length > 1) {
    return { ok: false, refusal: mixedInvocationRefusal(verb, flagged) };
  }

  // A positional naming no target — empty (`"$HUB"` with HUB unset), or a subtree's own folder
  // (`wayai-ws/hubs/$HUB`) — would reach its side as an empty selector, which the side read as none.
  if (positional === '') {
    return {
      ok: false,
      refusal: `\`wayai ${verb} ""\` names nothing, so it is refused rather than run on a folder it did not name.`,
    };
  }
  const fromPositional =
    positional !== undefined ? resolveSelectorSubtree(layout, positional, cwd) : undefined;
  if (
    positional !== undefined &&
    fromPositional &&
    fromPositional.kind !== 'both' &&
    fromPositional.kind !== 'unknown' &&
    fromPositional.target === ''
  ) {
    const { kind } = fromPositional;
    const { target, value } = SUBTREE_FLAGS.find(({ subtree }) => subtree === kind)!;
    const dir = `${SUBDIR[kind]}/`;
    return {
      ok: false,
      refusal: `\`wayai ${verb} ${sanitizeTerminalText(positional)}\` names ${dir}, not ${target} in it, so it is refused rather than run on a folder it did not name.\n  wayai ${verb} ${dir}${value}`,
    };
  }

  // (a) — a positional naming a folder of that name in each subtree.
  if (fromPositional?.kind === 'both') {
    return {
      ok: false,
      refusal: hubAndBaseRefusal(verb, fromPositional.target, fromPositional.target),
    };
  }

  // Rule 1. The flag wins over the positional — except when the two disagree
  // about the SUBTREE, which is refusal (a) rather than a silent precedence. A
  // positional disagreeing only about the target within the flag's own subtree
  // is the ordinary precedence the rule describes.
  const [flag] = flagged;
  if (flag) {
    if (fromPositional && fromPositional.kind !== 'unknown' && fromPositional.kind !== flag.subtree) {
      return {
        ok: false,
        refusal: mixedInvocationRefusal(verb, [flag, { subtree: fromPositional.kind, target: fromPositional.target }]),
      };
    }
    return { ok: true, subtree: flag.subtree, selector: flag.target };
  }

  // Rule 2.
  if (fromPositional && fromPositional.kind !== 'unknown') {
    return { ok: true, subtree: fromPositional.kind, selector: fromPositional.target };
  }
  // An unresolvable positional decides nothing, but is still the target the user
  // typed — carry it to whichever subtree the rules below select.
  const carried = positional;

  if (!gitRoot || !layout) return { ok: true, subtree: 'hubs', selector: carried };

  // Rule 3. cwd sits under at most one of the dirs, so this never refuses. A community
  // folder as cwd names its community only when nothing else was named: a bare name it
  // carried would otherwise be read as a community, which a bare name never is, so the
  // rules below route it as they do from any other directory.
  const { hubsDir, basesDir, communitiesDir } = layout;
  if (isUnder(hubsDir, cwd)) return { ok: true, subtree: 'hubs', selector: carried };
  if (isUnder(basesDir, cwd)) return { ok: true, subtree: 'bases', selector: carried };
  if (carried === undefined && isUnder(communitiesDir, cwd)) return { ok: true, subtree: 'communities' };

  // Rule 4. Declaring a default in each subtree leaves two candidates spanning
  // both — refusal (b), the same shape as an ambiguous workspace.
  const defaults = readRepoDefaults(gitRoot);
  if (defaults.default_hub && defaults.default_base) {
    return {
      ok: false,
      refusal: hubAndBaseRefusal(verb, defaults.default_hub, defaults.default_base),
    };
  }
  if (defaults.default_hub) return { ok: true, subtree: 'hubs', selector: carried ?? defaults.default_hub };
  if (defaults.default_base) return { ok: true, subtree: 'bases', selector: carried ?? defaults.default_base };

  // Rule 5.
  const candidates = listCandidates(layout);
  if (candidates.length === 1) {
    return { ok: true, subtree: candidates[0].subtree, selector: carried };
  }
  const hubCandidates = candidates.filter((c) => c.subtree === 'hubs');
  const baseCandidates = candidates.filter((c) => c.subtree === 'bases');
  // (b) — more than one candidate, spanning both subtrees.
  if (hubCandidates.length > 0 && baseCandidates.length > 0) {
    return {
      ok: false,
      refusal: hubAndBaseRefusal(verb, hubCandidates[0].name, baseCandidates[0].name),
    };
  }
  // More than one candidate WITHIN one subtree is that side's own ambiguity: it
  // keeps its existing message, which lists the candidates.
  if (baseCandidates.length > 0) return { ok: true, subtree: 'bases', selector: carried };
  return { ok: true, subtree: 'hubs', selector: carried };
}

/**
 * Route, or print the refusal and exit non-zero.
 *
 * Deliberately prints and exits rather than throwing: a mixed invocation is an
 * expected user condition, not a bug, so it must stay out of the Sentry pipeline
 * — the same posture as the worktree-scope guard. Callers run this FIRST, before
 * auth, before any request and before any file write, which is where the
 * refusal belongs.
 */
export function requireSubtree(
  verb: string,
  args: string[],
  gitRoot: string | null,
): { subtree: Subtree; selector?: string } {
  const result = routeSubtree({ verb, args, gitRoot });
  if (!result.ok) {
    console.error(result.refusal);
    process.exit(1);
  }
  return { subtree: result.subtree, selector: result.selector };
}
